import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const HERO_IMG =
    "https://images.unsplash.com/photo-1511454493857-0a29f2c023c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Hero() {
    const { t } = useLang();
    const go = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-36 lg:pt-44 pb-16 lg:pb-24 hex-pattern overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-end">
                {/* Left: copy */}
                <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="w-10 h-px bg-ink" />
                        <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                            {t.hero.kicker}
                        </span>
                    </div>

                    <h1
                        data-testid="hero-title"
                        className="font-display font-bold uppercase text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter text-ink"
                    >
                        {t.hero.title1}
                        <br />
                        <span className="relative inline-block">
                            <span className="relative z-10">
                                {t.hero.title2}
                            </span>
                            <span className="absolute -bottom-1 left-0 right-0 h-3 bg-mint -z-0" />
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className="mt-8 text-base md:text-lg text-ink-soft max-w-2xl leading-relaxed"
                    >
                        {t.hero.subtitle}
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <button
                            data-testid="hero-cta-audit"
                            onClick={() => go("contact")}
                            className="btn-primary group"
                        >
                            {t.hero.ctaPrimary}
                            <ArrowRight
                                size={16}
                                className="group-hover:translate-x-1 transition-transform"
                            />
                        </button>
                        <button
                            data-testid="hero-cta-services"
                            onClick={() => go("services")}
                            className="btn-secondary group"
                        >
                            {t.hero.ctaSecondary}
                            <ArrowDown
                                size={16}
                                className="group-hover:translate-y-1 transition-transform"
                            />
                        </button>
                    </div>
                </div>

                {/* Right: image card */}
                <div className="lg:col-span-5 relative">
                    <div className="relative">
                        <div className="absolute -inset-3 border border-ink hidden sm:block" />
                        <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-mint hidden sm:block" />
                        <img
                            src={HERO_IMG}
                            alt="Industrial maintenance"
                            className="relative w-full aspect-[4/5] object-cover grayscale"
                            data-testid="hero-image"
                        />
                        <div className="absolute bottom-6 left-6 right-6 bg-ink/90 text-white p-5 backdrop-blur-sm">
                            <div className="font-display text-xs uppercase tracking-widest text-mint">
                                SITMEC
                            </div>
                            <div className="font-display text-xl mt-1 tracking-wide">
                                {t.meta.tagline}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats row */}
            <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 lg:mt-28">
                <div className="border-t border-ink grid grid-cols-1 md:grid-cols-3">
                    {t.hero.stats.map((s, i) => (
                        <div
                            key={i}
                            data-testid={`hero-stat-${i}`}
                            className={`py-8 md:py-10 ${
                                i < t.hero.stats.length - 1
                                    ? "border-b md:border-b-0 md:border-r border-ink"
                                    : ""
                            } px-2 md:px-8`}
                        >
                            <div className="font-display font-bold text-4xl md:text-5xl text-ink tracking-tight">
                                {s.value}
                            </div>
                            <div className="text-sm text-ink-muted mt-2 max-w-xs">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
